import { ExclamationCircleOutlined } from "@ant-design/icons";
import { getMediaTypeIcon } from "../../helpers/mediaTypeIcons";
import { Grid, Modal, Tabs, Tooltip, Input } from "antd";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import {
  requestFilesServerPost,
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
  updateGlobalSearch,
} from "../../actions";
import MainLayout from "../../components/layouts/main";
import MediaLists from "../../components/views/MediaLists";
import PrimaryActionButton from "../../components/PrimaryActionButton";
import MediaPreviewPopup from "../../components/popups/MediaPreviewPopup";
import MediaReplacePopup from "../../components/popups/MediaReplacePopup";
import MediaUsageModalPopup from "../../components/popups/MediaUsageModalPopup";
import Constants from "../../data/constants";
import DataHelper from "../../helpers/DataHelper";
import PermissionManager from "../../managers/PermissionManager";
import PopupManager from "../../managers/PopupManager";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

// import './MediaPage.css'
import "antd/dist/antd.css";
import useFolderFileStore from "../../store/folderFileStore";
import useFileStore from "../../store/fileStore";
import {
  Image as MediaLibraryIcon,
  Upload as UploadIcon,
  Trash2 as Trash2Icon,
} from "lucide-react";

const { confirm } = Modal;
const { TabPane } = Tabs;

/**
 * Functional component equivalent of MediaPage class component.
 * @param {object} props - Component properties, including Redux state and actions.
 * @returns {JSX.Element}
 */
const MediaPage = (props) => {
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).media;
  const langRef = useRef(lang);
  useEffect(() => {
    langRef.current = lang;
  }, [lang]);
  const {
    selectedBrandData,
    requestServerGet,
    usertoken,
    globalSearch,
    requestServerPost,
    selectPage,
    updateGlobalSearch,
    onToggleSideMenu: toggleSideMenu, // Rename for clarity within FC
  } = props;

  // Detect if we are on the brand-level media page (no branch)
  const isBrandLevel = window.location.pathname.includes("/brand_media");
  const pageId = isBrandLevel
    ? Constants.BRAND_MEDIA_PAGE
    : Constants.MEDIA_PAGE;

  // --- Zustand Hook for folderDatas ---
  // Use the Zustand store to get the setter function
  const setFolderFileDatas = useFolderFileStore(
    (state) => state.setFolderFileDatas,
  );
  const selectedFolderID = useFolderFileStore(
    (state) => state.selectedFolderID,
  );
  const setSelectedFolderID = useFolderFileStore(
    (state) => state.setSelectedFolderID,
  );
  // Select the new action from the store
  const fetchFolderFileDatas = useFolderFileStore(
    (state) => state.fetchFolderFileDatas,
  );
  const fetchMediaDatas = useFileStore((state) => state.fetchMediaDatas);
  const mediaDatas = useFileStore((state) => state.mediaDatas);
  const setMediaDatas = useFileStore((state) => state.setMediaDatas);
  // --- State Initialization ---
  const [currentSelections, setCurrentSelections] = useState([]);
  // const [folderDatas, setFolderFileDatas] = useState([]); // Added from class component's updateMediaListData
  const [uploadPageVisible, setUploadPageVisible] = useState(false); // Retained from class state
  const [uploadProgress, setUploadProgress] = useState(0); // Retained from class state
  const [branches, setBranches] = useState([]);
  const [mediaUsageInfo, setMediaUsageInfo] = useState(null);

  // --- Ref Initialization ---
  const mediaPreviewPopupRef = useRef(null);
  const mediaReplacePopupRef = useRef(null);

  // The page header bar below sticks right under the top nav bar (56px
  // tall). Its own rendered height is measured so MediaLists can stick its
  // folder/media headers right below this header instead of underneath it
  // (which was cropping the header behind the nav bar / list headers).
  const NAV_BAR_HEIGHT = 56;
  const pageHeaderRef = useRef(null);
  const [pageHeaderHeight, setPageHeaderHeight] = useState(0);
  useEffect(() => {
    const el = pageHeaderRef.current;
    if (!el) return;
    const update = () => setPageHeaderHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const mediaListsStickyOffset = NAV_BAR_HEIGHT + pageHeaderHeight;

  // Refs for MediaLists to call resetPage (map to class refs)
  const allRef = useRef(null);
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const webRef = useRef(null);
  const streamRef = useRef(null);
  const videourlRef = useRef(null);

  // Mappable functions from class component, now using refs/state setters
  const clearFilesRef = useRef(() => {});
  const uploadProgressUpdateRef = useRef(() => {});

  // --- Data Fetching Logic ---

  /**
   * Fetches media and folder data for the current brand.
   */
  const updateMediaListData = useCallback(async () => {
    if (!selectedBrandData) {
      console.log("no brand data");
      setMediaDatas([]);
      return;
    }

    const params = { brandid: selectedBrandData.BrandID };

    console.log("getting files " + params.brandid);

    await fetchMediaDatas(selectedBrandData, props);

    fetchFolderFileDatas(selectedBrandData, props);

    if (
      isBrandLevel &&
      (selectedBrandData.ContentSharingMode === 2 ||
        selectedBrandData.ContentSharingMode === "2")
    ) {
      DataHelper.getBranches(
        { brandid: selectedBrandData.BrandID },
        props,
      ).then((data) => {
        setBranches(data || []);
      });
    } else {
      setBranches([]);
    }
  }, [selectedBrandData, isBrandLevel, props]);

  // --- Lifecycle/Effect Hooks ---

  useEffect(() => {
    setSelectedFolderID(null);
  }, []);
  // componentDidMount equivalent (Initial data fetch)
  useEffect(() => {
    if (selectedBrandData) {
      updateMediaListData();
    }
  }, [selectedBrandData, updateMediaListData]); // Re-run if selectedBrandData changes on initial load

  // componentDidUpdate equivalent for selectedBrandData change (handled by dependency array in initial useEffect, but keeping it clean)
  // Note: The previous logic relied on `componentDidUpdate(newProps)` comparing `this.props.selectedBrandData` to `newProps.selectedBrandData`.
  // The `useCallback` for `updateMediaListData` combined with the `useEffect` handles this dependency efficiently.

  // --- Selection Handlers ---

  const updateSelections = useCallback((newSelections, callback = () => {}) => {
    if (!newSelections) return;
    setCurrentSelections(newSelections);
    callback();
  }, []);

  const updateSelection = useCallback((id, value, callback = () => {}) => {
    setCurrentSelections((prevSelections) => {
      let newSelections;
      if (value) {
        // Add id, ensuring uniqueness (using Set logic)
        newSelections = [...new Set([...prevSelections, id])];
      } else {
        // Remove id
        newSelections = prevSelections.filter((d) => d !== id);
      }
      callback();
      return newSelections;
    });
  }, []);

  // --- Upload Handlers ---

  const onProgress = useCallback((percentCompleted) => {
    setUploadProgress(percentCompleted);
  }, []);

  const handleFilesUpload = (files) => {
    DataHelper.handleFilesUpload(
      files,
      props,
      uploadProgressUpdateRef.current,
      onProgress,
      uploadProgress,
    ).then(() => {
      updateMediaListData();
    });
  };

  // Mappers for external component functions (like in the class constructor)
  const mapClearFileFunction = (func) => {
    clearFilesRef.current = func;
  };

  const mapUploadProgressUpdate = (func) => {
    uploadProgressUpdateRef.current = func;
  };

  const showUploadPage = (doShow = true) => {
    if (doShow) {
      clearFilesRef.current();
    }
    setUploadPageVisible(doShow);
  };

  // --- Media Action Handlers ---

  const checkUsageAndDelete = useCallback(
    (fileids, brandid, onSuccessCallback = () => {}) => {
      const params = { fileids, brandid };

      requestServerPost(
        "check_files_usage",
        params,
        {},
        (response) => {
          const currentLang = langRef.current;
          const ct = (PRO_TRANSLATIONS[currentLang] || PRO_TRANSLATIONS.en)
            .media;
          if (response.data?.data?.length) {
            // Media is in use — MediaUsageModalPopup renders its own Modal,
            // so it's shown directly (not wrapped in Modal.info) to avoid
            // stacking two modals on top of each other.
            setMediaUsageInfo({
              lang: currentLang,
              data: response.data.data,
              onConfirmDelete: () => {
                requestServerPost(
                  "update_trashs",
                  params,
                  {},
                  () => {
                    onSuccessCallback();
                  },
                  usertoken,
                );
                setMediaUsageInfo(null);
              },
            });
          } else {
            // Media is not in use, confirm deletion
            confirm({
              title: ct.moveToTrash(fileids.split(",").length),
              icon: <ExclamationCircleOutlined />,
              onOk() {
                requestServerPost(
                  "update_trashs",
                  params,
                  {},
                  () => {
                    onSuccessCallback();
                  },
                  usertoken,
                );
              },
              onCancel() {
                // console.log('Cancel');
              },
            });
          }
        },
        usertoken,
      );
    },
    [requestServerPost, usertoken, selectPage],
  );

  const deleteMedia = useCallback(
    (data, callback = () => {}) => {
      if (!data || !data.FileID || !data.BrandID) return;
      checkUsageAndDelete(`${data.FileID}`, data.BrandID, callback);
    },
    [checkUsageAndDelete],
  );

  const deleteCurrentSelections = useCallback(
    (callback = () => {}) => {
      if (currentSelections.length === 0 || !selectedBrandData) return;

      const fileids = currentSelections.join(",");
      const brandid = selectedBrandData.BrandID;

      const onSuccessCallback = () => {
        setCurrentSelections([]);
        callback();
      };

      checkUsageAndDelete(fileids, brandid, onSuccessCallback);
    },
    [currentSelections, selectedBrandData, checkUsageAndDelete],
  );

  const previewMedia = useCallback((data) => {
    if (data && mediaPreviewPopupRef.current) {
      mediaPreviewPopupRef.current.previewMedia(data);
    }
  }, []);

  const replaceMedia = useCallback((data) => {
    if (data && mediaReplacePopupRef.current) {
      mediaReplacePopupRef.current.initReplaceMedia(data);
    }
  }, []);

  const editMedia = useCallback(
    (data) => {
      if (data) {
        const type = data.FileType?.toLocaleLowerCase();
        const isSharingMode2 =
          selectedBrandData &&
          (selectedBrandData.ContentSharingMode == 2 ||
            selectedBrandData.ContentSharingMode === "2");
        const editBranches = isSharingMode2 && isBrandLevel ? branches : [];
        switch (type) {
          case "image":
          case "video":
            PopupManager.launchEditImageVideoFilePopup(
              data,
              updateMediaListData,
              editBranches,
            );
            break;
          case "directoryurl":
          case "web":
          case "videourl":
            PopupManager.launchEditWebFilePopup(
              data,
              updateMediaListData,
              editBranches,
            );
            break;
          case "streamingurl":
            PopupManager.launchEditStreamingURLFilePopup(
              data,
              updateMediaListData,
              editBranches,
            );
            break;
          default:
          // Handle other types if necessary or do nothing
        }
      }
    },
    [updateMediaListData, branches, selectedBrandData, isBrandLevel],
  );

  const assignMedia = useCallback((data) => {
    if (data) {
      PopupManager.launchAssignFileToPlaylistsPopup(data, () => {});
    }
  }, []);

  // --- Permissions ---
  const createPermission = PermissionManager.checkPermission(
    isBrandLevel
      ? PermissionManager.PERMISSION_KEY_BRAND_MEDIA
      : PermissionManager.PERMISSION_KEY_MEDIA,
    PermissionManager.PERMISSION_TYPE_CREATE,
  );
  const editPermission = PermissionManager.checkPermission(
    isBrandLevel
      ? PermissionManager.PERMISSION_KEY_BRAND_MEDIA
      : PermissionManager.PERMISSION_KEY_MEDIA,
    PermissionManager.PERMISSION_TYPE_EDIT,
  );
  const deletePermission = PermissionManager.checkPermission(
    isBrandLevel
      ? PermissionManager.PERMISSION_KEY_BRAND_MEDIA
      : PermissionManager.PERMISSION_KEY_MEDIA,
    PermissionManager.PERMISSION_TYPE_DELETE,
  );

  const handleTabChange = () => {
    // Reset current page state (retained from class state logic)
    // Note: The original class component logic used `this.setState({ currentPage: 1 })`
    // followed by calling `resetPage()` on all refs.
    // We only need to call `resetPage()` on the refs, as MediaLists component handles its own pagination state.
    if (allRef.current) allRef.current.resetPage();
    if (imgRef.current) imgRef.current.resetPage();
    if (vidRef.current) vidRef.current.resetPage();
    if (webRef.current) webRef.current.resetPage();
    if (streamRef.current) streamRef.current.resetPage();
    if (videourlRef.current) videourlRef.current.resetPage();

    // Reset global search on tab change
    updateGlobalSearch("");
  };

  const screens = Grid.useBreakpoint();
  const isNarrow = screens.xl === false;
  const isMobile = screens.sm === false;

  const tabLabel = (icon, label) =>
    isNarrow ? (
      <Tooltip title={label}>{icon}</Tooltip>
    ) : (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {icon}
        <span>{label}</span>
      </span>
    );

  // --- Render ---
  return (
    <MainLayout
      id={pageId}
      quietTitleBar={true}
      style={{ padding: isMobile ? 10 : screens.xl ? 20 : 16 }}
      renderBreadCrumb={() => [
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "#94a3b8",
            padding: "4px 8px",
            lineHeight: "22px",
          }}
        >
          {t.title || "Media"}
        </span>,
      ]}
    >
      <div
        className="site-layout-background media-page-container"
        style={{
          margin: 0,
          minHeight: 280,
          backgroundColor: "transparent",
        }}
      >
        {/* ---- Page Header Bar ---- */}
        <div
          ref={pageHeaderRef}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? 12 : 8,
            background: "#fff",
            borderRadius: 16,
            padding: screens.xl ? "20px 24px" : isMobile ? "12px" : "12px 14px",
            border: "1px solid #e8ecf3",
            marginBottom: isMobile ? 12 : 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            position: "sticky",
            top: NAV_BAR_HEIGHT,
            zIndex: 100,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: screens.xl ? 16 : 12,
              flex: isMobile ? "1 1 100%" : 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: screens.xl ? 52 : 36,
                height: screens.xl ? 52 : 36,
                borderRadius: screens.xl ? 14 : 10,
                flexShrink: 0,
                background: "linear-gradient(135deg,#ec4899,#db2777)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MediaLibraryIcon size={screens.xl ? 26 : 18} color="#fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: screens.xl ? 22 : 15,
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t.title}
              </div>
              {screens.xl && (
                <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 3 }}>
                  {t.subtitle}
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: isMobile ? 8 : 6,
              alignItems: "center",
              flexWrap: "nowrap",
              flexShrink: 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              disabled={currentSelections.length === 0}
              onClick={() => deleteCurrentSelections(updateMediaListData)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: isMobile ? "10px 10px" : "8px 14px",
                borderRadius: 10,
                border: `1.5px solid ${currentSelections.length === 0 ? "#e2e8f0" : "#fca5a5"}`,
                background:
                  currentSelections.length === 0 ? "#f8fafc" : "#fff5f5",
                color: currentSelections.length === 0 ? "#94a3b8" : "#dc2626",
                fontSize: 13,
                fontWeight: 500,
                cursor:
                  currentSelections.length === 0 ? "not-allowed" : "pointer",
                flex: isMobile ? 1 : "none",
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Trash2Icon size={14} style={{ flexShrink: 0 }} />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isMobile ? t.deleteSelected?.split(" ")[0] : t.deleteSelected}
                {currentSelections.length
                  ? ` (${currentSelections.length})`
                  : ""}
              </span>
            </button>
            <PrimaryActionButton
              icon={<UploadIcon size={15} />}
              onClick={() =>
                PopupManager.launchFileUploader(
                  updateMediaListData,
                  selectedFolderID,
                )
              }
              style={
                isMobile
                  ? { flex: 1, minWidth: 0, paddingInline: 10 }
                  : undefined
              }
            >
              {t.uploadMedia}
            </PrimaryActionButton>
          </div>
        </div>
        <Tabs
          defaultActiveKey="1"
          style={{ width: "100%" }}
          centered
          onChange={handleTabChange}
        >
          <TabPane
            tab={tabLabel(getMediaTypeIcon("all", 14), t.tabAll)}
            key="1"
          >
            <MediaLists
              ref={allRef}
              updateSelections={updateSelections}
              updateSelection={updateSelection}
              currentSelections={currentSelections}
              onPreview={previewMedia}
              onReplace={replaceMedia}
              onEdit={editMedia}
              onAssign={assignMedia}
              onDelete={(data, callback = () => {}) => {
                deleteMedia(data, () => {
                  callback();
                  updateMediaListData();
                });
              }}
              mediaDatas={mediaDatas}
              type=""
              searchWord={globalSearch}
              allowEdit={editPermission}
              allowDelete={deletePermission}
              branches={branches}
              stickyOffset={mediaListsStickyOffset}
            />
          </TabPane>
          <TabPane
            tab={tabLabel(getMediaTypeIcon("image", 14), t.tabImage)}
            key="2"
          >
            <MediaLists
              ref={imgRef}
              updateSelections={updateSelections}
              updateSelection={updateSelection}
              currentSelections={currentSelections}
              onPreview={previewMedia}
              onReplace={replaceMedia}
              onEdit={editMedia}
              onAssign={assignMedia}
              onDelete={(data, callback = () => {}) => {
                deleteMedia(data, () => {
                  callback();
                  updateMediaListData();
                });
              }}
              mediaDatas={mediaDatas}
              type="image"
              searchWord={globalSearch}
              allowEdit={editPermission}
              allowDelete={deletePermission}
              branches={branches}
              stickyOffset={mediaListsStickyOffset}
            />
          </TabPane>
          <TabPane
            tab={tabLabel(getMediaTypeIcon("video", 14), t.tabVideo)}
            key="3"
          >
            <MediaLists
              ref={vidRef}
              updateSelections={updateSelections}
              updateSelection={updateSelection}
              currentSelections={currentSelections}
              onPreview={previewMedia}
              onReplace={replaceMedia}
              onEdit={editMedia}
              onAssign={assignMedia}
              onDelete={(data, callback = () => {}) => {
                deleteMedia(data, () => {
                  callback();
                  updateMediaListData();
                });
              }}
              mediaDatas={mediaDatas}
              type="video"
              searchWord={globalSearch}
              allowEdit={editPermission}
              allowDelete={deletePermission}
              branches={branches}
              stickyOffset={mediaListsStickyOffset}
            />
          </TabPane>
          <TabPane
            tab={tabLabel(getMediaTypeIcon("web", 14), t.tabWebsite)}
            key="4"
          >
            <MediaLists
              ref={webRef}
              updateSelections={updateSelections}
              updateSelection={updateSelection}
              currentSelections={currentSelections}
              onPreview={previewMedia}
              onReplace={replaceMedia}
              onEdit={editMedia}
              onAssign={assignMedia}
              onDelete={(data, callback = () => {}) => {
                deleteMedia(data, () => {
                  callback();
                  updateMediaListData();
                });
              }}
              mediaDatas={mediaDatas}
              type="web"
              searchWord={globalSearch}
              allowEdit={editPermission}
              allowDelete={deletePermission}
              branches={branches}
              stickyOffset={mediaListsStickyOffset}
            />
          </TabPane>
          <TabPane
            tab={tabLabel(getMediaTypeIcon("streamingurl", 14), t.tabStreaming)}
            key="6"
          >
            <MediaLists
              ref={streamRef}
              updateSelections={updateSelections}
              updateSelection={updateSelection}
              currentSelections={currentSelections}
              onPreview={previewMedia}
              onReplace={replaceMedia}
              onEdit={editMedia}
              onAssign={assignMedia}
              onDelete={(data, callback = () => {}) => {
                deleteMedia(data, () => {
                  callback();
                  updateMediaListData();
                });
              }}
              mediaDatas={mediaDatas}
              type="streamingurl"
              searchWord={globalSearch}
              allowEdit={editPermission}
              allowDelete={deletePermission}
              branches={branches}
              stickyOffset={mediaListsStickyOffset}
            />
          </TabPane>
          <TabPane
            tab={tabLabel(getMediaTypeIcon("videourl", 14), t.tabVideoUrl)}
            key="7"
          >
            <MediaLists
              ref={videourlRef}
              updateSelections={updateSelections}
              updateSelection={updateSelection}
              currentSelections={currentSelections}
              onPreview={previewMedia}
              onReplace={replaceMedia}
              onEdit={editMedia}
              onAssign={assignMedia}
              onDelete={(data, callback = () => {}) => {
                deleteMedia(data, () => {
                  callback();
                  updateMediaListData();
                });
              }}
              mediaDatas={mediaDatas}
              type="videourl"
              searchWord={globalSearch}
              allowEdit={editPermission}
              allowDelete={deletePermission}
              branches={branches}
              stickyOffset={mediaListsStickyOffset}
            />
          </TabPane>
        </Tabs>

        <div className="bottom-margin"></div>
        <MediaPreviewPopup ref={mediaPreviewPopupRef}></MediaPreviewPopup>
        <MediaReplacePopup
          parentProps={props}
          ref={mediaReplacePopupRef}
        ></MediaReplacePopup>
        <MediaUsageModalPopup
          lang={mediaUsageInfo?.lang}
          mediaUsage={mediaUsageInfo?.data || []}
          onClose={() => setMediaUsageInfo(null)}
          onConfirmDelete={() => mediaUsageInfo?.onConfirmDelete?.()}
          onGotoPlaylist={(playlistID) => {
            setMediaUsageInfo(null);
            selectPage(Constants.PLAYLIST_PAGE, { id: playlistID });
          }}
        />
      </div>
    </MainLayout>
  );
};

// --- Redux Connection (remains the same) ---
const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    globalSearch: state.globalSearch,
    selectedBrandData: state.selectedBrandData,
    selectedBranchData: state.selectedBranchData,
  };
};

export default connect(mapStateToProps, {
  updateGlobalSearch,
  requestFilesServerPost,
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
})(MediaPage);
